import { useState } from "react";
import { Download, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";
import { Seo } from "../components/Seo";
import { Reveal, Hairline } from "../components/motion";
import { api, BACKEND_URL } from "../lib/api";
import { MEDIA } from "../lib/site";

const GuideSuccess = ({ g, downloadUrl }) => (
  <div data-testid="guide-success" className="p-8 bg-ink-800 border border-gold/20">
    <div className="flex items-center gap-3 text-gold mb-4">
      <Check size={20} strokeWidth={1.5} /> <span className="font-serif text-2xl">{g.success}</span>
    </div>
    {downloadUrl && (
      <>
        <p className="text-sand text-sm mb-6">{g.successNote}</p>
        <a href={downloadUrl} target="_blank" rel="noreferrer" data-testid="guide-download" className="btn-gold btn-caps inline-flex">
          <Download size={14} strokeWidth={1.5} /> {g.download}
        </a>
      </>
    )}
  </div>
);

const GuideForm = ({ g, form, setForm, sending, onSubmit }) => (
  <form onSubmit={onSubmit} data-testid="guide-form" className="space-y-8 max-w-md">
    <div className="field">
      <label className="label-caps block mb-2">{g.name}</label>
      <input data-testid="guide-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
    </div>
    <div className="field">
      <label className="label-caps block mb-2">{g.email}</label>
      <input data-testid="guide-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
    </div>
    <label className="flex items-start gap-3 cursor-pointer group">
      <input data-testid="guide-gdpr" type="checkbox" checked={form.gdpr} onChange={(e) => setForm({ ...form, gdpr: e.target.checked })} className="mt-1 h-4 w-4 accent-[#D4AF37] shrink-0" />
      <span className="text-xs text-sand leading-relaxed group-hover:text-cream transition-colors">{g.gdpr}</span>
    </label>
    <button data-testid="guide-submit" type="submit" disabled={sending} className="btn-solid btn-caps disabled:opacity-50">
      {sending ? g.sending : g.submit}
    </button>
  </form>
);

const GuideCover = () => (
  <Reveal className="order-last lg:order-first">
    <div className="relative max-w-md">
      <div className="absolute -inset-4 border border-gold/20" />
      <img src={MEDIA.guideCover} alt="" className="relative w-full aspect-square object-cover" />
    </div>
  </Reveal>
);

export default function Guia() {
  const { lang, t } = useLang();
  const g = t.guide.form;
  const [form, setForm] = useState({ name: "", email: "", lang, gdpr: false });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr) { toast.error(g.error); return; }
    setSending(true);
    try {
      const { data } = await api.post("/guide", form);
      setDone(true);
      if (data.download) setDownloadUrl(`${BACKEND_URL}${data.download}`);
      toast.success(g.success);
    } catch {
      toast.error(g.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title={lang === "pt" ? "Guia das Responsabilidades Parentais — Maria João Nepomuceno" : "Guide to Parental Responsibilities — Maria João Nepomuceno"}
        description={t.guide.form.gdpr}
        pageKey="guia"
      />
      <section className="pt-28 pb-16 md:pt-40 md:pb-24 bg-ink min-h-[70vh]">
        <div className="container-lux grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <GuideCover />

          <div>
            <div className="overline mb-6">{t.guide.overline}</div>
            <h1 className="font-serif text-4xl md:text-6xl font-light text-cream leading-tight">
              {lang === "pt" ? "Guia das Responsabilidades Parentais" : "Guide to Parental Responsibilities"}
            </h1>
            <Hairline className="my-8 max-w-xs" />
            {done ? (
              <GuideSuccess g={g} downloadUrl={downloadUrl} />
            ) : (
              <GuideForm g={g} form={form} setForm={setForm} sending={sending} onSubmit={submit} />
            )}
          </div>
        </div>
      </section>
    </>
  );
}
